import React, { useMemo, useState } from "react";
import { NotificationManager } from "react-notifications";
import { httpPost } from "../../components/helpers/httpMethods";
import { setLocalItem } from "../../components/helpers/authService";
import AppLogo from "../../assets/images/appLogoV2.png";
import "./authPages.css";

// Reads the email and expiry from the reset link (display only; the server does the real check).
const readToken = (token) => {
  try {
    const part = String(token).split(".")[1];
    const padded = part + "===".slice((part.length + 3) % 4);
    return JSON.parse(atob(padded.replace(/-/g, "+").replace(/_/g, "/")));
  } catch (e) {
    return null;
  }
};

const Check = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12l5 5 9-10" />
  </svg>
);

const LockIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

export default function InputNewPassword({ match, history }) {
  const token = match.params.id;
  const info = useMemo(() => readToken(token), [token]);
  const email = info && info.email ? info.email : "";
  const linkExpired = !info || (info.exp && info.exp * 1000 < Date.now());

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [state, setState] = useState(linkExpired ? "expired" : "form"); // form | expired | done

  const rules = [
    { ok: password.length >= 8, text: "At least 8 characters" },
    { ok: /[A-Za-z]/.test(password) && /\d/.test(password), text: "Contains a letter and a number" },
    { ok: password.length > 0 && password === confirm, text: "Both passwords match" },
  ];
  const valid = rules.every((r) => r.ok);

  const goLogin = () => history.push("/login");
  const requestNewLink = () =>
    history.push(`/forgot_password${email ? `?email=${encodeURIComponent(email)}` : ""}`);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError("");

    const res = await httpPost(`auth/user/reset_password/${token}`, { password });
    if (!res || res.er) {
      setBusy(false);
      const msg = (res && res.er && (res.er.message || String(res.er))) || "Something went wrong. Please try again.";
      if (/expired|already been used/i.test(msg)) setState("expired");
      else setError(msg);
      return;
    }

    // Log straight in with the new password, exactly as the login page does.
    const loginEmail = (res.data && res.data.email) || email;
    const login = loginEmail ? await httpPost("auth/user_login", { email: loginEmail, password }) : null;
    setBusy(false);
    if (login && !login.er && login.data && login.data.token) {
      localStorage.setItem("token", login.data.token);
      setLocalItem("user", login.data.user);
      localStorage.setItem("expiredToken", "false");
      NotificationManager.success("Your password has been changed. Welcome back!");
      history.push("/");
    } else {
      setState("done");
    }
  };

  return (
    <main className="ap-page">
      <div className="ap-card">
        <button type="button" className="ap-logo" onClick={() => history.push("/")} aria-label="TheNewspaperStand home">
          <img src={AppLogo} alt="TheNewspaperStand" />
        </button>

        {state === "expired" && (
          <>
            <h1 className="ap-title">This link has expired</h1>
            <p className="ap-sub">
              Password reset links work for 1 hour and can only be used once. Request a new one and we’ll email it to you straight away.
            </p>
            <button type="button" className="ap-btn" onClick={requestNewLink}>Send me a new link</button>
            <p className="ap-footer">
              Remembered your password? <button type="button" className="ap-link" onClick={goLogin}>Log in</button>
            </p>
          </>
        )}

        {state === "done" && (
          <>
            <h1 className="ap-title">Password updated</h1>
            <p className="ap-sub">Your password has been changed. You can now log in with your new password.</p>
            <button type="button" className="ap-btn" onClick={goLogin}>Log in</button>
          </>
        )}

        {state === "form" && (
          <form className="ap-form" onSubmit={submit} noValidate>
            <span className="ap-icon"><LockIcon /></span>
            <h1 className="ap-title">Set a new password</h1>
            <p className="ap-sub">
              {email ? <>For <strong>{email}</strong></> : "Choose a new password for your account."}
            </p>

            <div className="ap-field">
              <label className="ap-label" htmlFor="ap-new">New password</label>
              <div className="ap-input-wrap">
                <input
                  id="ap-new"
                  className="ap-input"
                  type={show ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                />
                <button type="button" className="ap-toggle" onClick={() => setShow((v) => !v)} aria-pressed={show}>
                  {show ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <div className="ap-field">
              <label className="ap-label" htmlFor="ap-confirm">Confirm new password</label>
              <div className={`ap-input-wrap${confirm && confirm !== password ? " is-error" : ""}`}>
                <input
                  id="ap-confirm"
                  className="ap-input"
                  type={show ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>
            </div>

            <ul className="ap-rules" aria-live="polite">
              {rules.map((r) => (
                <li key={r.text} className={`ap-rule${r.ok ? " is-ok" : ""}`}>
                  <span className="ap-dot">{r.ok ? <Check /> : null}</span>
                  {r.text}
                </li>
              ))}
            </ul>

            {error ? <p className="ap-error" role="alert">{error}</p> : null}

            <button type="submit" className="ap-btn" disabled={!valid || busy}>
              {busy ? "Updating…" : "Update password"}
            </button>
            <p className="ap-footer">
              <button type="button" className="ap-link" onClick={goLogin}>Back to log in</button>
            </p>
          </form>
        )}
      </div>
    </main>
  );
}
